import React, { useContext, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { DriverPreference, ShipmentPackagingType, ShipmentWeightUnit } from "app/pages/shipments/types/domain";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import { selectManyCargoGroups } from "features/cargo-groups/store/cargoGroupsSlice";
import { useCargoGroupsSliceSelector } from "features/cargo-groups/store/cargoGroupsStore";
import useLDFlag from "hooks/useLDFlag";
import { FormProvider, useForm } from "react-hook-form";
import { TrailerSizeEnum } from "types/TrailerSize";
import { TrailerTypeEnum } from "types/TrailerType";

import { useFinalizeAndSendQr } from "../_hooks/useFinalizeAndSendQr";
import { useQuoteRequestSelector } from "../_store";
import { QuoteStepIndex } from "../types";
import { LoadAttributesStep, ReviewStep, TeamMembersStep } from "./_components";
import { LocationStep } from "./_components/LocationStep";
import PartnersStep from "./_components/PartnersStep";
import {
  FinalSchema,
  getLoadAttributesSchema,
  getPartnersSchema,
  getTeamSchema,
  locationSchema,
  StopSchema,
} from "./_schemas";

export const QuoteRequestFTLForm: React.FC = ({}) => {
  const { quoteRequest: airQuoteRequest, stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { finalizeAndSendQr, resetLocalState } = useFinalizeAndSendQr();
  const enableFTLCargoGroups = useLDFlag("ftlCargoGroups");

  const schemaMap = useMemo(
    () => ({
      [QuoteStepIndex.Location]: locationSchema,
      [QuoteStepIndex.LoadAttributes]: getLoadAttributesSchema(!!enableFTLCargoGroups),
      [QuoteStepIndex.Partners]: getPartnersSchema(!!enableFTLCargoGroups),
      [QuoteStepIndex.Team]: getTeamSchema(!!enableFTLCargoGroups),
      [QuoteStepIndex.Review]: getTeamSchema(!!enableFTLCargoGroups),
    }),
    [enableFTLCargoGroups]
  );

  const { topLevelCargoGroupIds } = useContext(CargoGroupContext);

  const cargoGroups = useCargoGroupsSliceSelector((state) => selectManyCargoGroups(state, topLevelCargoGroupIds));

  // @ts-expect-error this is fine
  const currentSchema = schemaMap[stepIndex];

  const methods = useForm<FinalSchema>({
    mode: "onSubmit",
    resolver: zodResolver(currentSchema),
    values: {
      reference_number: airQuoteRequest.reference_number ?? undefined,
      internal_reference_number: airQuoteRequest.internal_reference_number ?? undefined,
      hide_internal_ref_till_dispatch: airQuoteRequest.hide_internal_ref_till_dispatch ?? false,
      deadline_respond_at: airQuoteRequest.deadline_respond_at ?? undefined,
      pallet_type: airQuoteRequest.load_spec?.pallet_type,
      unit_count: airQuoteRequest.load_spec?.unit_count,
      stops: (airQuoteRequest.stops as StopSchema[]) ?? [{}, {}],
      commodities: airQuoteRequest.load_spec?.commodities ?? "",
      driver_preference: airQuoteRequest.load_spec?.driver_preference ?? DriverPreference.Solo,
      is_hazardous: airQuoteRequest.is_hazardous ?? false,
      trailer_type: (airQuoteRequest.load_spec?.trailer_type as TrailerTypeEnum) ?? TrailerTypeEnum.DRY_VAN,
      is_palletized: (airQuoteRequest.load_spec?.is_palletized as false) ?? false,
      total_weight: airQuoteRequest.load_spec?.total_weight,
      weight_per_unit: airQuoteRequest.load_spec?.weight_per_unit,
      weight_unit: airQuoteRequest.load_spec?.weight_unit ?? ShipmentWeightUnit.Lb,
      packaging_type: airQuoteRequest.load_spec?.packaging_type as ShipmentPackagingType,
      packing_count: airQuoteRequest.load_spec?.packing_count as number,
      pallet_count: airQuoteRequest.load_spec?.pallet_count as number,
      trailer_size: airQuoteRequest.load_spec?.trailer_size as TrailerSizeEnum,
      truck_quantity: airQuoteRequest.load_spec?.truck_quantity ?? 1,
      goods_value: airQuoteRequest.goods_value ?? undefined,
      hazardous_notes: airQuoteRequest.hazardous_goods_details ?? undefined,
      notes: airQuoteRequest.note ?? undefined,
      min_temp: airQuoteRequest.load_spec?.min_temp ?? undefined,
      max_temp: airQuoteRequest.load_spec?.max_temp ?? undefined,
      partners: airQuoteRequest.partners ?? [],
      teammates: airQuoteRequest.teammates,
      // @ts-expect-error this typing is fine, it errors becuase it doesn't work for some variants of the schema
      cargo_groups: (cargoGroups as unknown as FinalSchema["cargo_groups"]) ?? [],
    },
  });

  const onSubmit = async () => {
    return finalizeAndSendQr();
  };

  if (resetLocalState) {
    return null;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <LocationStep onSubmit={onSubmit} />
        <LoadAttributesStep onSubmit={onSubmit} />
        <PartnersStep onSubmit={onSubmit} />
        <TeamMembersStep onSubmit={onSubmit} />
        <ReviewStep onSubmit={onSubmit} />
      </FormProvider>
    </div>
  );
};

export default QuoteRequestFTLForm;
